import { useEffect, useMemo, useState } from "react";
import type { RouterProviderProps } from "react-router/dom";

import * as Sentry from "@sentry/react";

import Loading from "@components/Utility/Loading";
import {
  attemptStaleReload,
  isChunkLoadError,
} from "@features/appVersion/deployVersionGuard";
import {
  useAuthRoutingResources,
  useAuthSession,
} from "@features/auth/AuthUserContext";
import { useAuthZPermissions } from "@features/authz/AuthZContext";
import isMobile from "@utils/mobileDetect";

import { BootErrorFallback } from "./BootErrorFallback";
import { type RouteFactories, RouterMount } from "./RouterMount";
import { resolveRouterTarget, routerTargetKey } from "./resolveRouterTarget";

type RouteModuleState =
  | { status: "loading" }
  | { status: "ready"; routes: RouteFactories }
  | { status: "error"; error: unknown };

async function loadRouteFactories(): Promise<RouteFactories> {
  if (isMobile) {
    // DO NOT REMOVE
    // This console log is needed for `root` not to be loaded on mobile.
    console.info("loading mobile routes");
    return import("./mobileRoutes/mobileRoot");
  }

  return import("./routes/root");
}

function useRouteFactories(): RouteModuleState {
  const [state, setState] = useState<RouteModuleState>({ status: "loading" });

  useEffect(() => {
    let active = true;

    void loadRouteFactories()
      .then((routes) => {
        if (active) {
          setState({ status: "ready", routes });
        }
      })
      .catch((error: unknown) => {
        if (isChunkLoadError(error) && attemptStaleReload()) {
          return;
        }

        console.error("Brandhub failed to load [route-import]", error);
        Sentry.captureException(error, {
          tags: { bootstrapSource: "route-import" },
        });
        if (active) {
          setState({ status: "error", error });
        }
      });

    return () => {
      active = false;
    };
  }, []);

  return state;
}

export function AppRouterCoordinator({
  ...routerProviderProps
}: Omit<RouterProviderProps, "router">) {
  const session = useAuthSession();
  const resources = useAuthRoutingResources();
  const permissions = useAuthZPermissions();
  const routeModule = useRouteFactories();

  // The key hashes every granted ID, so both are kept off the render path.
  // Free at one permission; not at hundreds.
  const resolution = useMemo(
    () => resolveRouterTarget({ session, permissions, ...resources }),
    [session, permissions, resources]
  );
  const targetKey = useMemo(
    () =>
      resolution.status === "resolved"
        ? routerTargetKey(resolution.target)
        : null,
    [resolution]
  );

  if (resolution.status === "pending" || routeModule.status === "loading") {
    return <Loading />;
  }
  if (resolution.status === "error" || routeModule.status === "error") {
    return <BootErrorFallback />;
  }

  return (
    <RouterMount
      key={targetKey}
      target={resolution.target}
      routes={routeModule.routes}
      {...routerProviderProps}
    />
  );
}
